import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import Label from '../../atoms/Label';
import Input from '../../atoms/Input';
import SuggestedUserName from '../../atoms/SuggestedUserName';
import { useDebounce } from '../../../hooks/useDebounce';
import {
  useSuggestUsernamesMutation,
  useValidateUsernameMutation,
} from '../../../store/services/addSellerService';

interface Props {
  dataTestId?: string;
  disabled?: boolean;
  skipUsernameUniquenessCheck?: boolean;
}

const SignupUsernameField = ({
  dataTestId,
  disabled,
  skipUsernameUniquenessCheck,
}: Props) => {
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    formState: { errors },
    control,
    setError,
    clearErrors,
    getFieldState,
  } = useFormContext();

  const [suggestedUserNames, setSuggestedUserNames] = useState<
    string[] | undefined
  >();

  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const username = watch('username');
  const usernameState = getFieldState('username');
  const debouncedValue = useDebounce(username, 500);
  const hasFetchedRef = useRef(false);

  const [suggestUsernames, { isLoading: suggestUsernamesLoading }] =
    useSuggestUsernamesMutation();

  const [validateUsername] = useValidateUsernameMutation();

  // Reset suggestions when name changes
  useEffect(() => {
    hasFetchedRef.current = false;
    setSuggestedUserNames([]);
  }, [firstName, lastName]);

  // Validate username uniqueness on debounced change
  useEffect(() => {
    if (skipUsernameUniquenessCheck) return;

    const runValidation = async () => {
      try {
        const res = await validateUsername({ username: debouncedValue }).unwrap();
        if (res?.payload === false) {
          setError('username', {
            type: 'validate',
            message: t('components.validations.usernameExists'),
          });
        } else {
          clearErrors('username');
        }
      } catch (err: unknown) {
        const error = err as { status?: number; data?: { message?: string; errors?: Record<string, string> } };
        
        

        setError('username', {
          type: 'validate',
          message:
            error?.data?.errors?.username ||
            error?.data?.message ||
            t('components.validations.usernameExists'),
        });
      }
    };

    if (debouncedValue && !usernameState?.invalid) {
      runValidation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, usernameState?.invalid, skipUsernameUniquenessCheck]);

  const fieldFocus = async () => {
    if (disabled || hasFetchedRef.current) return;
    if (
      firstName &&
      lastName &&
      /^[a-zA-Z\s]+$/.test(firstName) &&
      /^[a-zA-Z\s]+$/.test(lastName)
    ) {
      hasFetchedRef.current = true;
      try {
        const res = await suggestUsernames({ firstName, lastName }).unwrap();
        setSuggestedUserNames(res?.payload);
      } catch {
        // silently ignore suggestion errors
      }
    }
  };

  const changeValue = (value: string) => {
    setValue('username', value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <div data-testid="signup-username-field">
      <Label
        text={t('sellers.addSeller.username')}
        htmlFor="username"
      />

      <Controller
        name="username"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            data-testid={dataTestId || 'signup-username-input'}
            status={errors.username?.message ? 'error' : undefined}
            helperText={errors.username?.message as string}
            id="username"
            type="text"
            name="username"
            autoComplete="off"
            placeholder={t('sellers.addSeller.usernamePlaceholder')}
            onFocus={fieldFocus}
            onBlur={() => {
              if (usernameState.invalid) {
                setError('username', {
                  message: usernameState.error?.message,
                });
              }
            }}
            value={username}
            disabled={disabled}
          />
        )}
      />

      {suggestUsernamesLoading && (
        <div className="mt-2" data-testid="username-suggestions-loading">
          <Spin indicator={<LoadingOutlined spin />} size="small" />
        </div>
      )}

      {!!suggestedUserNames?.length && (
        <div className="mt-2" data-testid="username-suggestions-container">
          <Label
            id="suggest-label"
            text={t('sellers.addSeller.suggestedUsernames')}
          />
          <div className="flex gap-2 flex-wrap hide-scrollbare">
            {suggestedUserNames.map((name, idx) => (
              <SuggestedUserName
                key={idx}
                onMouseDown={() => changeValue(name)}
                text={`@${name}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SignupUsernameField;
